import { Card, Badge } from "react-bootstrap";
import { Truck, ShoppingCart, Package, Users } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchDispatchMonitoring } from "slices/Dashboard/Dispatch_Summary/thunk";
import "./DispatchSummary.css";
import { Axios } from "api/axios";
import { api } from "helpers/api_url";
import { WarehouseOption } from "./DashboardFilters";

interface DispatchSummaryProps {
  dateFrom?: Date;
  dateTo?: Date;
  warehouse?: WarehouseOption[];
}

export function DispatchSummary({ dateFrom, dateTo, warehouse = [] }: DispatchSummaryProps) {
  const dispatch = useDispatch<any>();
  const [dailyDispatchData, setDailyDispatchData] = useState([])

  // Get dispatch monitoring data from Redux store
  const dispatchMonitoring = useSelector((state: any) => state.DispatchSummaryReducer);
  const { dispatch_done_count, stock_transfer_count, issue_to_labour_count, total, loading } = dispatchMonitoring || {};


  // Fetch dispatch monitoring data on component mount
  useEffect(() => {
    dispatch(fetchDispatchMonitoring({
      dateFrom,
      dateTo,
      warehouse,
    }));
  }, [dispatch, dateFrom, dateTo, warehouse]);

  useEffect(() => {
    const params = new URLSearchParams();

    // Add date filters
    if (dateFrom) {
      params.append("date_from", dateFrom.toISOString().split('T')[0]);
    }
    if (dateTo) {
      params.append("date_to", dateTo.toISOString().split('T')[0]);
    }

    // Add warehouse filters
    if (warehouse && warehouse.length > 0) {
      const warehouseIds = warehouse
        .map((w: any) => w.warehouse_id)
        .filter((id: any) => id !== "all");

      if (warehouseIds.length > 0) {
        if (warehouseIds.length === 1) {
          params.append("warehouse_id", warehouseIds[0].toString());
        } else {
          params.append("warehouse_id", warehouseIds.join(','));
        }
      }
    }

    const url = `${api?.dashboad?.dispatch_monitoring_daily_activity}${params.toString() ? `?${params.toString()}` : ''}`;
    Axios.get(url).then((response) => {
      setDailyDispatchData(response.data?.data);
    }).catch((error) => {
      console.error("Error fetching daily dispatch data:", error);
    });
  }, [dateFrom, dateTo, warehouse]);
  // Calculate dispatch stats with percentages
  const dispatchStats = useMemo(() => {
    const fromOrderTotal = dispatch_done_count || 0;
    const stockTransferTotal = stock_transfer_count || 0;
    const issueToLabourTotal = issue_to_labour_count || 0;
    const totalCount = total || 1; // Avoid division by zero

    return {
      fromOrder: {
        total: fromOrderTotal,
        percentage: totalCount > 0 ? Math.round((fromOrderTotal / totalCount) * 100) : 0,
      },
      stockTransfer: {
        total: stockTransferTotal,
        percentage: totalCount > 0 ? Math.round((stockTransferTotal / totalCount) * 100) : 0,
      },
      issueToLabour: {
        total: issueToLabourTotal,
        percentage: totalCount > 0 ? Math.round((issueToLabourTotal / totalCount) * 100) : 0,
      },
    };
  }, [dispatch_done_count, stock_transfer_count, issue_to_labour_count, total]);

  // Calculate total dispatch count from dailyDispatchData
  const totalDispatchCount = useMemo(() => {
    if (!dailyDispatchData || dailyDispatchData.length === 0) return 0;
    return dailyDispatchData.reduce((sum: number, day: any) => {
      const dayTotal = (day.fromOrder || 0) + (day.stockTransfer || 0) + (day.issueToLabour || 0);
      return sum + dayTotal;
    }, 0);
  }, [dailyDispatchData]);

  console.log("Total Dispatch Count:", dispatchStats);
  // Calculate max value for Y-axis scaling - dynamically scales based on data
  const yAxisDomain = useMemo(() => {
    if (!dailyDispatchData || dailyDispatchData.length === 0) return [0, 20];
    // Calculate the maximum stacked value (sum of all bars per day)
    const maxDayTotal = Math.max(
      ...dailyDispatchData.map((day: any) => {
        return (day.fromOrder || 0) + (day.stockTransfer || 0) + (day.issueToLabour || 0);
      })
    );
    // Add 20% padding and round up to nearest 10, minimum 20
    const maxValue = Math.max(20, Math.ceil(maxDayTotal * 1.2 / 10) * 10);
    return [0, maxValue];
  }, [dailyDispatchData]);

  return (
    <Card className="inventory-summary-card shadow-sm border-0">
      <Card.Body className="p-4">
        {/* Header */}
        <div className="mb-3 pb-2 border-bottom position-relative">
          <div className="order-summary-title-container">
            <Truck className="order-summary-icon" />
            <h3 className="order-summary-title">Dispatch Monitoring</h3>
          </div>
        </div>

      {/* Dispatch Type Stats Grid */}
      <div className="dispatch-summary-stats-grid">
        <div className="dispatch-summary-stat-card dispatch-summary-stat-card-blue">
          <div className="dispatch-summary-stat-card-header">
            <ShoppingCart className="dispatch-summary-stat-card-icon dispatch-summary-stat-card-icon-blue" />
            <span className="dispatch-summary-stat-card-label dispatch-summary-stat-card-label-blue">Dispatch from Order</span>
          </div>
          {loading ? (
            <div className="dispatch-summary-stat-card-value-skeleton"></div>
          ) : (
            <p className="dispatch-summary-stat-card-value">{dispatchStats.fromOrder.total}</p>
          )}
          <div className="dispatch-summary-stat-card-footer">
            <p className="dispatch-summary-stat-card-footer-label"> From <b>{dateFrom?.toISOString()?.split('T')[0]}</b> to <b>{dateTo?.toISOString()?.split('T')[0]}</b></p>
            {loading ? (
              <div className="dispatch-summary-stat-card-badge-skeleton"></div>
            ) : (
              <span className="dispatch-summary-stat-card-badge dispatch-summary-stat-card-badge-blue">{dispatchStats.fromOrder.percentage}%</span>
            )}
          </div>
        </div>

        <div className="dispatch-summary-stat-card dispatch-summary-stat-card-purple">
          <div className="dispatch-summary-stat-card-header">
            <Truck className="dispatch-summary-stat-card-icon dispatch-summary-stat-card-icon-purple" />
            <span className="dispatch-summary-stat-card-label dispatch-summary-stat-card-label-purple">Stock Transfer</span>
          </div>
          {loading ? (
            <div className="dispatch-summary-stat-card-value-skeleton"></div>
          ) : (
            <p className="dispatch-summary-stat-card-value">{dispatchStats.stockTransfer.total}</p>
          )}
          <div className="dispatch-summary-stat-card-footer">
            <p className="dispatch-summary-stat-card-footer-label"> From <b>{dateFrom?.toISOString()?.split('T')[0]}</b> to <b>{dateTo?.toISOString()?.split('T')[0]}</b></p>
            {loading ? (
              <div className="dispatch-summary-stat-card-badge-skeleton"></div>
            ) : (
              <span className="dispatch-summary-stat-card-badge dispatch-summary-stat-card-badge-purple">{dispatchStats.stockTransfer.percentage}%</span>
            )}
          </div>
        </div>

        <div className="dispatch-summary-stat-card dispatch-summary-stat-card-orange">
          <div className="dispatch-summary-stat-card-header">
            <Users className="dispatch-summary-stat-card-icon dispatch-summary-stat-card-icon-orange" />
            <span className="dispatch-summary-stat-card-label dispatch-summary-stat-card-label-orange">Issue to Labour</span>
          </div>
          {loading ? (
            <div className="dispatch-summary-stat-card-value-skeleton"></div>
          ) : (
            <p className="dispatch-summary-stat-card-value">{dispatchStats.issueToLabour.total}</p>
          )}
          <div className="dispatch-summary-stat-card-footer">
             <p className="dispatch-summary-stat-card-footer-label"> From <b>{dateFrom?.toISOString()?.split('T')[0]}</b> to <b>{dateTo?.toISOString()?.split('T')[0]}</b></p>
            {loading ? (
              <div className="dispatch-summary-stat-card-badge-skeleton"></div>
            ) : (
              <span className="dispatch-summary-stat-card-badge dispatch-summary-stat-card-badge-orange">{dispatchStats.issueToLabour.percentage}%</span>
            )}
          </div>
        </div>
      </div>

      {/* Daily Dispatch Chart */}
      <div className="dispatch-summary-chart-container no-hover-chart">
        <h4 className="dispatch-summary-chart-title">Dispatch Activity by Type (Last 7 Days)</h4>
        <ResponsiveContainer width="100%" height={240}>
          <BarChart
            data={dailyDispatchData}
            onMouseEnter={() => {}}
            onMouseLeave={() => {}}
            style={{ userSelect: 'none' }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="day" />
            <YAxis domain={yAxisDomain} allowDecimals={false} />
            <Tooltip />
            <Legend />
            <Bar
              dataKey="fromOrder"
              name="From Order"
              fill="#3b82f6"
              stackId="a"
              radius={[0, 0, 0, 0]}
              style={{ cursor: 'default' }}
            />
            <Bar
              dataKey="stockTransfer"
              name="Stock Transfer"
              fill="#a855f7"
              stackId="a"
              radius={[0, 0, 0, 0]}
              style={{ cursor: 'default' }}
            />
            <Bar
              dataKey="issueToLabour"
              name="Issue to Labour"
              fill="#f97316"
              stackId="a"
              radius={[8, 8, 0, 0]}
              style={{ cursor: 'default' }}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Total Dispatch Summary */}
      <div className="dispatch-summary-total">
        <div className="dispatch-summary-total-content">
          <Package className="dispatch-summary-total-icon" />
          <div className="dispatch-summary-total-text-container">
            <p className="dispatch-summary-total-label">Total Dispatches Last 7 Days</p>
            <p className="dispatch-summary-total-sublabel">Across all dispatch types</p>
          </div>
        </div>
        <div className="dispatch-summary-total-value-container">
          {loading ? (
            <div className="dispatch-summary-total-value-skeleton"></div>
          ) : (
            <p className="dispatch-summary-total-value">{totalDispatchCount}</p>
            // <p className="dispatch-summary-total-value">{Number(dispatchStats.fromOrder.total)+Number(dispatchStats.stockTransfer.total)+Number(dispatchStats.issueToLabour.total)}</p>
          )}
          <p className="dispatch-summary-total-value-label">Items dispatched</p>
        </div>
      </div>
      </Card.Body>
    </Card>
  );
}